import Vue from 'vue';
import router from '../../../routers/router';
import resetState from '@/helpers/resetState';
import { getTransactionPromise } from '@/helpers/transaction';
import store from '@/store';
import { ABI } from './ABI';
import { Contract } from 'web3-eth-contract';

const FEATURE_NAME = 'OwnerFeature';

let from;

async function contract(): Promise<Contract> {
  from = {from: store.getters.getWallet};
  const address = store.getters.getFeatureAddress(FEATURE_NAME);
  return new Promise(resolve => {
    let contract = new window.web3.eth.Contract(ABI, address);
    resolve(contract);
  });
}

const initialState = {
  isInitiatedFirstOwner: false,
  isInitiatedOwners: false,
  isReader: false,
  ownerCounter: null,
  owners: {},
  baseAllOwners: {}
};

const state = {
  isInitiatedFirstOwner: initialState.isInitiatedFirstOwner,
  isInitiatedOwners: initialState.isInitiatedOwners,
  isReader: initialState.isReader,
  ownerCounter: initialState.ownerCounter,
  owners: initialState.owners,
  baseAllOwners: initialState.baseAllOwners
};
const getters = {
  getOwnerCounter: state => state.ownerCounter,
  getOwners: state => state.owners,
  getInitStatusFirstOwner: state => state.isInitiatedFirstOwner,
  getInitStatusOwners: state => state.isInitiatedOwners,
  isOwner: (state, getters) => address => {
    let walletAddress = address;
    if (!address) walletAddress = getters.getWallet;
    if (state.isReader) return true;
    return !!state.owners[walletAddress];
  },
  isReader: state => state.isReader,
  isUsedAddress: (state, getters) => address => {
    let walletAddress = address.toLowerCase();
    if (
      getters.isOwner(walletAddress) ||
      getters.isMember(walletAddress) ||
      getters.getCompanyInfo.wallet === walletAddress
    ) {
      return true;
    }
    return false;
  }
};

const mutations = {
  setOwnerCounter: (state, item) => {
    state.ownerCounter = item;
  },
  setAnyOwner: (state, item) => {
    Vue.set(state.baseAllOwners, item.address, item);
    if (item.status === 3) Vue.set(state.owners, item.address, item);
  },
  setInitStatusFirstOwner: (state, item) => {
    state.isInitiatedFirstOwner = item;
  },
  setInitStatusOwners: (state, item) => {
    state.isInitiatedOwners = item;
  },
  setReader: (state, item) => {
    state.isReader = item;
  },
  resetOwners: state => {
    Vue.set(state, 'owners', {});
  },
  resetOwnerFeature: state => {
    resetState(state, initialState);
  }
};

const actions = {
  initOwnerFeature({ dispatch, commit }) {
    dispatch('isInitiatedFirstOwnerDapp');
    dispatch('isInitiatedOwnersDapp');
    dispatch('isReaderDapp');
    dispatch('getOwnersDapp');

    contract().then(contract => {
      let transactionHash = '0x';
      contract.events.OwnerEvent().subscribe((err, res) => {
        if (!err && transactionHash !== res.transactionHash) {
          transactionHash = res.transactionHash;
          let status = parseInt(res.returnValues.status);
          if (!err) {
            dispatch('getOwnersDapp');
          }

          commit('initializationPending', false);

          // Owner initialization completed
          if (status === 11) {
            console.log('Owner initialization completed');
            router.push('/');
            dispatch('isInitiatedFirstOwnerDapp');
          }
          // Initialization completed
          if (status === 12) {
            console.log('Initialization completed');
            router.push('/');
            window.location.reload();
            dispatch('isInitiatedOwnersDapp');
          }
        }
      });
    });
  },
  getOwnersDapp({ dispatch }) {
    dispatch('getOwnerCounterDapp').then(result => {
      for (let i = 0; i < result; i++) {
        dispatch('getOwnerDapp', i);
      }
    });
  },
  /**
   * Get count of owners
   * @return {Promise<number>}
   * @res "count of owners" {string}
   */
  getOwnerCounterDapp({ commit }) {
    return new Promise(resolve => {
      contract().then(contract => {
        contract.methods.getOwnerCounter()
          .call()
          .then(res => {
            commit('setOwnerCounter', res);
            resolve(res);
          })
          .catch(console.error);
      });
    });
  },
  /**
   * Get owner
   * @param id {number}
   * @res[0] "status" {number}
   * @res[1] "address" {string}
   * @res[2] "proposer" {string}
   * @res[3] "blocker" {string}
   * @res[4] "regTime" {number}
   * @res[4] "approvers" {Array}
   */
  getOwnerDapp({ commit }, id) {
    contract().then(contract => {
      contract.methods.getOwner(id)
        .call()
        .then(res => {
          let data = {
            id,
            status: parseInt(res[0]),
            address: res[1],
            proposer: res[2],
            blocker: res[3],
            regTime: parseInt(res[4]),
            approvers: res[5]
          };
          commit('setAnyOwner', data);
        })
        .catch(console.error);
    });
  },
  /**
   * Change owner
   * @param oldAddress {string}
   * @param newAddress {string}
   * @return {Promise<void>}
   */
  changeOwnerDapp({ getters }, addr) {
    return new Promise((resolve, reject) => {
      contract().then(contract => {
        contract.methods.changeOwner(getters.getWallet, addr)
          .send(from)
          .then(res => {
            router.push('/');
            location.reload();
            return getTransactionPromise(resolve, reject, undefined, res)
          })
          .catch(console.error);
      });
    });
  },
  /**
   * Is first owner initialized
   */
  isInitiatedFirstOwnerDapp({ commit }) {
    contract().then(contract => {
      contract.methods.isInitiatedFirstOwner()
        .call()
        .then(res => {
          commit('setInitStatusFirstOwner', res);
        })
        .catch(console.error);
    });
  },
  /**
   * Is owners initialized
   */
  isInitiatedOwnersDapp({ commit }) {
    contract().then(contract => {
      contract.methods.isInitiatedOwners()
        .call()
        .then(res => {
          commit('setInitStatusOwners', res);
        })
        .catch(console.error);
    });
  },
  /**
   * Initiate Owners
   * @return {Promise<void>}
   */
  initiateOwnersDapp({}) {
    return new Promise((resolve, reject) => {
      contract().then(contract => {
        contract.methods.initiateOwners()
          .send(from)
          .then(res => getTransactionPromise(resolve, reject, undefined, res))
          .catch(console.error);
      });
    });
  },
  /**
   * Wallet belongs to an agent
   * @param address {string}s
   */
  isReaderDapp({ getters, commit }) {
    contract().then(contract => {
      contract.methods.isReader(getters.getWallet)
        .call()
        .then((res) => {
          commit('setReader', res);
        })
        .catch(console.error);
    });
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
